import React, { useState, useEffect, useRef } from 'react';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  Plane, Hotel, Car, Users, Calendar, Mail, Shield, Zap,
  Clock, Globe, CheckCircle, X, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  Sparkles, Target, TrendingUp, Award, MessageSquare, FileText,
  Bell, Lock, UserCheck, Settings, BarChart3, Headphones, Rocket
} from 'lucide-react';
import EarlyAccessForm from './EarlyAccessForm';

// Feature categories for the landing page
const FEATURE_CATEGORIES = [
  {
    id: 'travel_management',
    title: 'Comprehensive Travel Management',
    description: 'All your executive travel needs in one place',
    icon: Globe,
    features: [
      { key: 'commercial_flight', label: 'Commercial Flights', description: 'Track and manage commercial flight bookings' },
      { key: 'private_flight', label: 'Private Aviation', description: 'Private jet and charter flight coordination' },
      { key: 'hotel', label: 'Hotel Accommodations', description: 'Hotel bookings and preferences management' },
      { key: 'chauffeur_service', label: 'Chauffeur Services', description: 'Ground transportation and car services' },
      { key: 'rental_car', label: 'Rental Cars', description: 'Vehicle rental tracking and management' },
      { key: 'restaurant', label: 'Restaurant Reservations', description: 'Dining bookings and preferences' },
      { key: 'meeting', label: 'Meetings & Events', description: 'Business meeting coordination' }
    ]
  },
  {
    id: 'automation',
    title: 'Smart Automation',
    description: 'AI-powered features that save you time',
    icon: Zap,
    features: [
      { key: 'flight_verification', label: 'Flight Verification', description: 'Automatic flight status verification via FlightAware' },
      { key: 'flight_alerts', label: 'Flight Alerts', description: 'Real-time flight status notifications' },
      { key: 'document_parsing', label: 'Document Parsing', description: 'AI extraction of travel details from emails and documents' },
      { key: 'dedicated_emails', label: 'Dedicated Email Addresses', description: 'Automatic itinerary capture via dedicated inboxes' }
    ]
  },
  {
    id: 'team_features',
    title: 'Team Collaboration',
    description: 'Work together seamlessly',
    icon: Users,
    features: [
      { key: 'teams', label: 'Teams & Permissions', description: 'Organize EAs into teams with role-based access' },
      { key: 'rbac', label: 'Role-Based Access Control', description: 'Granular permission management for organization users' },
      { key: 'sso', label: 'Single Sign-On (SSO)', description: 'Google and Microsoft SSO integration' },
      { key: 'saml_sso', label: 'SAML 2.0 SSO', description: 'Enterprise SAML SSO (Okta, Azure AD, PingIdentity, etc.)' },
      { key: 'multiple_eas', label: 'Multiple Executive Assistants', description: 'Support for multiple EAs per executive' }
    ]
  }
];

// Feature mapping for package comparison
const FEATURE_MAP = {
  // Component types
  'commercial_flight': { key: 'available_components', check: (pkg) => pkg.available_components?.includes('commercial_flight') },
  'private_flight': { key: 'available_components', check: (pkg) => pkg.available_components?.includes('private_flight') },
  'hotel': { key: 'available_components', check: (pkg) => pkg.available_components?.includes('hotel') },
  'chauffeur_service': { key: 'available_components', check: (pkg) => pkg.available_components?.includes('chauffeur_service') },
  'rental_car': { key: 'available_components', check: (pkg) => pkg.available_components?.includes('rental_car') },
  'restaurant': { key: 'available_components', check: (pkg) => pkg.available_components?.includes('restaurant') },
  'meeting': { key: 'available_components', check: (pkg) => pkg.available_components?.includes('meeting') },
  'event': { key: 'available_components', check: (pkg) => pkg.available_components?.includes('event') },
  'memo': { key: 'available_components', check: (pkg) => pkg.available_components?.includes('memo') },
  // Feature flags
  'flight_verification': { key: 'flight_verification_enabled', check: (pkg) => pkg.flight_verification_enabled },
  'flight_alerts': { key: 'flight_alerts_config', check: (pkg) => pkg.flight_alerts_config?.enabled },
  'document_parsing': { key: 'document_parsing_config', check: (pkg) => pkg.document_parsing_config?.period === 'unlimited' || pkg.document_parsing_config?.limit > 0 },
  'dedicated_emails_exec': { key: 'dedicated_emails_executives', check: (pkg) => pkg.dedicated_emails_executives },
  'dedicated_emails_ea': { key: 'dedicated_emails_eas', check: (pkg) => pkg.dedicated_emails_eas },
  'teams': { key: 'teams_enabled', check: (pkg) => pkg.teams_enabled },
  'rbac': { key: 'rbac_enabled', check: (pkg) => pkg.rbac_enabled },
  'sso': { key: 'sso_enabled', check: (pkg) => pkg.sso_enabled },
  'saml_sso': { key: 'saml_sso_enabled', check: (pkg) => pkg.saml_sso_enabled },
  'oidc_sso': { key: 'oidc_sso_enabled', check: (pkg) => pkg.oidc_sso_enabled },
  'dedicated_emails': { key: 'dedicated_emails', check: (pkg) => pkg.dedicated_emails_executives || pkg.dedicated_emails_eas },
  'multiple_eas': { key: 'ea_config', check: (pkg) => {
    const config = pkg.ea_config;
    if (!config) return false;
    if (config.limit_type === 'unlimited') return true;
    if (config.limit_type === 'included_plus_extra') return true;
    // For 'fixed' type, only show checkmark if more than 1 EA is allowed
    return (config.max_count || 1) > 1;
  }},
  'audit_logs': { key: 'audit_logs_enabled', check: (pkg) => pkg.audit_logs_enabled },
  'urbanride_quickbook': { key: 'urbanride_quickbook_enabled', check: (pkg) => pkg.urbanride_quickbook_enabled }
};

// Comparison features for the pricing table
const COMPARISON_FEATURES = [
  { category: 'Travel Components', features: [
    { key: 'commercial_flight', label: 'Commercial Flights' },
    { key: 'private_flight', label: 'Private Aviation' },
    { key: 'hotel', label: 'Hotel Management' },
    { key: 'chauffeur_service', label: 'Chauffeur Services' },
    { key: 'rental_car', label: 'Rental Cars' },
    { key: 'restaurant', label: 'Restaurant Reservations' },
    { key: 'meeting', label: 'Meetings & Events' }
  ]},
  { category: 'Automation & Intelligence', features: [
    { key: 'flight_verification', label: 'Flight Verification' },
    { key: 'flight_alerts', label: 'Real-time Flight Alerts' },
    { key: 'document_parsing', label: 'AI Document Parsing' },
    { key: 'dedicated_emails', label: 'Dedicated Email Inboxes' },
    { key: 'awardwallet', label: 'AwardWallet Integration' }
  ]},
  { category: 'Teams', features: [
    { key: 'teams', label: 'Teams & Permissions' },
    { key: 'multiple_eas', label: 'Multiple EAs per Executive' }
  ]},
  { category: 'Enterprise', features: [
    { key: 'rbac', label: 'Role-Based Access Control (RBAC)' },
    { key: 'sso', label: 'Single Sign-On (Google/Microsoft)' },
    { key: 'saml_sso', label: 'SAML 2.0 SSO' },
    { key: 'oidc_sso', label: 'OIDC SSO' },
    { key: 'audit_logs', label: 'Audit Logs' },
    { key: 'urbanride_quickbook', label: 'Urbanride Quickbook' }
  ]}
];

const LandingPageV2 = ({ publicMode = false } = {}) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [showEarlyAccessForm, setShowEarlyAccessForm] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const carouselRef = useRef(null);
  const pageRef = useRef(null);

  useEffect(() => {
    fetchPackages();
  }, []);

  // Check scroll position for carousel navigation
  const checkScrollPosition = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // Scroll carousel left or right
  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const cardWidth = 384; // Approximate width of a package card
      const scrollAmount = direction === 'left' ? -cardWidth : cardWidth;
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Update scroll position when packages load
  useEffect(() => {
    if (packages.length > 3) {
      // Small delay to ensure DOM is ready
      setTimeout(checkScrollPosition, 100);
    }
  }, [packages]);

  const fetchPackages = async () => {
    try {
      const endpoint = publicMode ? '/landing/public-packages' : '/landing/packages';
      const response = await fetch(`${getApiBase()}${endpoint}`, {
        headers: publicMode ? { 'Content-Type': 'application/json' } : getAuthHeaders(),
        credentials: publicMode ? 'omit' : 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPackages(data.packages || []);
      } else {
        setError('Failed to load packages');
      }
    } catch (err) {
      console.error('Failed to fetch packages:', err);
      setError('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const checkFeature = (pkg, featureKey) => {
    const mapping = FEATURE_MAP[featureKey];
    if (!mapping) return false;
    return mapping.check(pkg);
  };

  // Get display value for metered features in comparison table
  const getFeatureDisplay = (pkg, featureKey) => {
    // Handle metered features with specific values
    if (featureKey === 'document_parsing') {
      const config = pkg.document_parsing_config;
      if (!config || (config.period !== 'unlimited' && config.limit <= 0)) {
        return { type: 'none' };
      }
      if (config.period === 'unlimited') {
        return { type: 'text', value: 'Unlimited' };
      }
      return { type: 'text', value: `${config.limit}/${config.period}` };
    }

    if (featureKey === 'flight_alerts') {
      const config = pkg.flight_alerts_config;
      if (!config?.enabled) {
        return { type: 'none' };
      }
      if (config.period === 'unlimited') {
        return { type: 'text', value: 'Unlimited' };
      }
      return { type: 'text', value: `${config.limit}/${config.period}` };
    }

    if (featureKey === 'multiple_eas') {
      const config = pkg.ea_config;
      if (!config) {
        return { type: 'none' };  // No config means only 1 EA - show X
      }
      if (config.limit_type === 'unlimited') {
        return { type: 'text', value: 'Unlimited' };
      }
      if (config.limit_type === 'included_plus_extra') {
        return { type: 'text', value: `${config.included_count || 1}+` };
      }
      // For fixed type, show X if only 1 EA allowed
      const maxCount = config.max_count || 1;
      if (maxCount <= 1) {
        return { type: 'none' };
      }
      return { type: 'text', value: `${maxCount}` };
    }

    // For non-metered features, return check/none
    return { type: checkFeature(pkg, featureKey) ? 'check' : 'none' };
  };

  // Check if any package has a specific feature enabled
  const anyPackageHasFeature = (featureKey) => {
    return packages.some(pkg => {
      const display = getFeatureDisplay(pkg, featureKey);
      return display.type !== 'none';
    });
  };

  const formatPrice = (price, currency = 'USD') => {
    if (price === 0) return 'Free';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    }).format(price);
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  // FAQ items - updated for beta launch
  const faqItems = [
    {
      question: 'How does executive travel management work?',
      answer: 'Our platform provides a centralized hub for managing all aspects of executive travel. Executive assistants can track flights, hotels, ground transportation, and meetings in real-time. AI-powered features automatically parse travel confirmations and provide live flight updates.'
    },
    {
      question: 'Can I import existing travel bookings?',
      answer: 'Yes! You can forward confirmation emails to your dedicated inbox, and our AI will automatically extract and organize the travel details. You can also manually add components or import from various travel booking platforms.'
    },
    {
      question: 'How does the flight verification work?',
      answer: 'We integrate with FlightAware to provide real-time verification and status updates for commercial flights. When you add a flight, we automatically verify the flight details and can send alerts for delays, gate changes, or cancellations. Note that private and charter flights are typically not available for real-time tracking due to privacy restrictions.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely. We use enterprise-grade security including encryption at rest and in transit, role-based access controls, and optional SSO integration. Your executive travel data is protected with the highest security standards.'
    },
    {
      question: 'Can multiple assistants manage the same executive?',
      answer: 'Yes! Our team features allow multiple EAs to collaborate on managing executive travel. You can set up teams, assign permissions, and ensure seamless handoffs between assistants.'
    },
    {
      question: 'What does the beta program include?',
      answer: 'As a beta participant, you get early access to our full platform with all features enabled. We value your feedback as we refine the product before general availability. Beta participants receive priority support and will have input on feature development.'
    }
  ];

  return (
    <div ref={pageRef} className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-40"></div>
        {/* Top navigation */}
        <nav className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {!logoFailed && (
              <img src="/veyage-logo-dark.svg" alt="Veyage" className="h-10" onError={() => setLogoFailed(true)} />
            )}
            {logoFailed && (
              <span className="text-white font-semibold tracking-wide">Veyage</span>
            )}
          </div>
          <a
            href="/login"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white/90 hover:text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors"
          >
            Sign in
          </a>
        </nav>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 pt-12 lg:pt-16 lg:pb-32">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-purple-500/20 border border-purple-400/30 rounded-full px-4 py-2 mb-8">
              <Rocket className="w-4 h-4 text-purple-300" />
              <span className="text-sm text-purple-200">Beta Launch - Early Access</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Streamline Executive Travel<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                with Intelligence
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto mb-10">
              The complete platform for executive assistants to manage travel itineraries,
              track flights in real-time, and ensure seamless journeys for busy executives.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => scrollToSection('pricing')}
                className="inline-flex items-center justify-center px-8 py-4 bg-purple-600 hover:bg-purple-500 rounded-lg font-semibold text-lg transition-all shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40"
              >
                View Plans
                <ChevronDown className="w-5 h-5 ml-2" />
              </button>
              <button
                onClick={() => scrollToSection('features')}
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg font-semibold text-lg transition-all"
              >
                Explore Features
              </button>
            </div>
          </div>
        </div>
        {/* Decorative gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent"></div>
      </section>

      {/* Stats Section - Updated for Beta */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-slate-900">7+</div>
              <div className="text-sm text-slate-500 mt-1">Travel Components</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-slate-900">AI-Powered</div>
              <div className="text-sm text-slate-500 mt-1">Document Parsing</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-slate-900">Real-time</div>
              <div className="text-sm text-slate-500 mt-1">Flight Tracking</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-slate-900">Enterprise</div>
              <div className="text-sm text-slate-500 mt-1">Security & SSO</div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Executive Travel is Complex.<br />
                Managing it Shouldn't Be.
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <X className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Scattered Information</h3>
                    <p className="text-slate-600">Travel details spread across emails, apps, and spreadsheets</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <X className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Manual Updates</h3>
                    <p className="text-slate-600">Constantly checking flight statuses and updating itineraries</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <X className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Coordination Chaos</h3>
                    <p className="text-slate-600">Difficulty coordinating between multiple EAs and stakeholders</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-200">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 rounded-full px-3 py-1 text-sm font-medium mb-6">
                <CheckCircle className="w-4 h-4" />
                The Solution
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">One Platform for Everything</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Target className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Centralized Dashboard</h4>
                    <p className="text-slate-600">All travel components in one unified view</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Automatic Updates</h4>
                    <p className="text-slate-600">Real-time commercial flight tracking and instant notifications</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Team Collaboration</h4>
                    <p className="text-slate-600">Seamless handoffs and shared visibility</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Everything You Need to Manage Executive Travel
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From booking management to real-time flight tracking, our platform provides
              all the tools executive assistants need.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature Cards */}
            <div className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <Plane className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Flight Management</h3>
              <p className="text-slate-600">
                Track commercial flights with real-time status updates,
                gate changes, and delay notifications. Coordinate private aviation bookings with detailed itinerary management.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <Hotel className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Hotel & Accommodation</h3>
              <p className="text-slate-600">
                Manage hotel bookings, track preferences, and maintain a
                comprehensive history of accommodations.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <Car className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Ground Transportation</h3>
              <p className="text-slate-600">
                Coordinate chauffeur services, rental cars, and all ground
                transportation needs seamlessly.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">AI Document Parsing</h3>
              <p className="text-slate-600">
                Forward confirmation emails and let AI automatically extract
                and organize travel details.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mb-4">
                <Bell className="w-6 h-6 text-pink-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Real-time Alerts</h3>
              <p className="text-slate-600">
                Get instant notifications for commercial flight delays, cancellations,
                gate changes, and more.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Enterprise Security</h3>
              <p className="text-slate-600">
                SAML 2.0 and OIDC single sign-on, role-based access controls,
                comprehensive audit logs, and enterprise-grade security.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 rounded-full px-4 py-2 text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              Beta Pricing
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Choose the Right Plan for Your Team
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From individual executive assistants to enterprise teams,
              we have a plan that fits your needs.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-slate-500">
              {error}
            </div>
          ) : packages.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <p>No packages available yet. Check back soon!</p>
            </div>
          ) : (
            <>
              {/* Package Cards */}
              {packages.length > 3 ? (
                /* Carousel layout for more than 3 packages */
                <div className="relative mb-16">
                  {/* Left scroll button */}
                  {canScrollLeft && (
                    <button
                      onClick={() => scrollCarousel('left')}
                      className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
                      aria-label="Scroll left"
                    >
                      <ChevronLeft className="w-6 h-6 text-slate-600" />
                    </button>
                  )}

                  {/* Carousel container */}
                  <div
                    ref={carouselRef}
                    onScroll={checkScrollPosition}
                    className="flex gap-6 overflow-x-auto scroll-smooth pb-4 px-2 snap-x snap-mandatory"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                  >
                    <style>{`div::-webkit-scrollbar { display: none; }`}</style>
                    {packages.map((pkg, index) => (
                      <div
                        key={pkg.id}
                        className={`flex-shrink-0 w-[340px] bg-white rounded-2xl p-8 shadow-lg border-2 transition-all hover:shadow-xl snap-center ${
                          index === 1 && packages.length >= 2 ? 'border-purple-500 relative' : 'border-slate-200'
                        }`}
                      >
                        {index === 1 && packages.length >= 2 && (
                          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                            <span className="bg-purple-600 text-white text-sm font-medium px-4 py-1 rounded-full">
                              Most Popular
                            </span>
                          </div>
                        )}
                        <div className="text-center mb-6">
                          <h3 className="text-xl font-bold text-slate-900 mb-2">{pkg.name}</h3>
                          {pkg.description && (
                            <p className="text-slate-600 text-sm">{pkg.description}</p>
                          )}
                        </div>
                        <div className="text-center mb-6">
                          {pkg.hide_price ? (
                            <>
                              <span className="text-3xl font-bold text-slate-900">Contact Us</span>
                              <p className="text-sm text-slate-500 mt-2">Custom pricing for your needs</p>
                            </>
                          ) : (
                            <>
                              <span className="text-4xl font-bold text-slate-900">
                                {formatPrice(pkg.base_price, pkg.currency)}
                              </span>
                              {pkg.base_price > 0 && (
                                <span className="text-slate-500">/month</span>
                              )}
                              {pkg.is_trial && pkg.trial_days && (
                                <div className="mt-2 text-sm text-purple-600">
                                  {pkg.trial_days}-day free trial
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        <div className="space-y-3 mb-8">
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-slate-700">
                              {pkg.executive_config?.limit_type === 'unlimited'
                                ? 'Unlimited Executives'
                                : `${pkg.executive_config?.included_count || 1} Executive${(pkg.executive_config?.included_count || 1) > 1 ? 's' : ''} included`
                              }
                            </span>
                          </div>
                          {pkg.ea_config && (
                            <div className="flex items-center gap-2 text-sm">
                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <span className="text-slate-700">
                                {pkg.ea_config.limit_type === 'unlimited'
                                  ? 'Unlimited EAs per executive'
                                  : pkg.ea_config.limit_type === 'included_plus_extra'
                                    ? `${pkg.ea_config.included_count || 1} EA${(pkg.ea_config.included_count || 1) > 1 ? 's' : ''} included${pkg.ea_config.cost_per_additional ? ` (+${formatPrice(pkg.ea_config.cost_per_additional, pkg.currency)}/additional)` : ''}`
                                    : pkg.ea_config.max_count > 1
                                      ? `Up to ${pkg.ea_config.max_count} EAs per executive`
                                      : '1 EA per executive'
                                }
                              </span>
                            </div>
                          )}
                          {(pkg.document_parsing_config?.period === 'unlimited' || pkg.document_parsing_config?.limit > 0) && (
                            <div className="flex items-center gap-2 text-sm">
                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <span className="text-slate-700">
                                {pkg.document_parsing_config.period === 'unlimited'
                                  ? 'Unlimited document parsing'
                                  : `${pkg.document_parsing_config.limit} document parses/${pkg.document_parsing_config.period}`
                                }
                              </span>
                            </div>
                          )}
                          {pkg.flight_verification_enabled && (
                            <div className="flex items-center gap-2 text-sm">
                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <span className="text-slate-700">Flight verification</span>
                            </div>
                          )}
                          {pkg.flight_alerts_config?.enabled && (
                            <div className="flex items-center gap-2 text-sm">
                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <span className="text-slate-700">
                                {pkg.flight_alerts_config.period === 'unlimited'
                                  ? 'Unlimited flight alerts'
                                  : `${pkg.flight_alerts_config.limit} flight alerts/${pkg.flight_alerts_config.period}`
                                }
                              </span>
                            </div>
                          )}
                          {pkg.teams_enabled && (
                            <div className="flex items-center gap-2 text-sm">
                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <span className="text-slate-700">Team collaboration</span>
                            </div>
                          )}
                          {pkg.rbac_enabled && (
                            <div className="flex items-center gap-2 text-sm">
                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <span className="text-slate-700">RBAC permissions</span>
                            </div>
                          )}
                          {pkg.sso_enabled && (
                            <div className="flex items-center gap-2 text-sm">
                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <span className="text-slate-700">SSO integration</span>
                            </div>
                          )}
                          {pkg.saml_sso_enabled && (
                            <div className="flex items-center gap-2 text-sm">
                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <span className="text-slate-700">SAML 2.0 SSO</span>
                            </div>
                          )}
                          {pkg.oidc_sso_enabled && (
                            <div className="flex items-center gap-2 text-sm">
                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <span className="text-slate-700">OIDC SSO</span>
                            </div>
                          )}
                          {pkg.audit_logs_enabled && (
                            <div className="flex items-center gap-2 text-sm">
                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <span className="text-slate-700">Audit logs</span>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => setShowEarlyAccessForm(true)}
                          className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                            index === 1 && packages.length >= 2
                              ? 'bg-purple-600 hover:bg-purple-700 text-white'
                              : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                          }`}
                        >
                          Get Started
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Right scroll button */}
                  {canScrollRight && (
                    <button
                      onClick={() => scrollCarousel('right')}
                      className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
                      aria-label="Scroll right"
                    >
                      <ChevronRight className="w-6 h-6 text-slate-600" />
                    </button>
                  )}

                  {/* Scroll indicator dots */}
                  <div className="flex justify-center gap-2 mt-4">
                    {packages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          if (carouselRef.current) {
                            const cardWidth = 340 + 24; // card width + gap
                            carouselRef.current.scrollTo({ left: index * cardWidth, behavior: 'smooth' });
                          }
                        }}
                        className="w-2 h-2 rounded-full bg-slate-300 hover:bg-slate-400 transition-colors"
                        aria-label={`Go to package ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                /* Grid layout for 3 or fewer packages */
                <div className={`grid gap-8 mb-16 ${packages.length === 1 ? 'max-w-md mx-auto' : packages.length === 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
                  {packages.map((pkg, index) => (
                    <div
                      key={pkg.id}
                      className={`bg-white rounded-2xl p-8 shadow-lg border-2 transition-all hover:shadow-xl ${
                        index === 1 && packages.length >= 2 ? 'border-purple-500 relative' : 'border-slate-200'
                      }`}
                    >
                      {index === 1 && packages.length >= 2 && (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                          <span className="bg-purple-600 text-white text-sm font-medium px-4 py-1 rounded-full">
                            Most Popular
                          </span>
                        </div>
                      )}
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-slate-900 mb-2">{pkg.name}</h3>
                        {pkg.description && (
                          <p className="text-slate-600 text-sm">{pkg.description}</p>
                        )}
                      </div>
                      <div className="text-center mb-6">
                        {pkg.hide_price ? (
                          <>
                            <span className="text-3xl font-bold text-slate-900">Contact Us</span>
                            <p className="text-sm text-slate-500 mt-2">Custom pricing for your needs</p>
                          </>
                        ) : (
                          <>
                            <span className="text-4xl font-bold text-slate-900">
                              {formatPrice(pkg.base_price, pkg.currency)}
                            </span>
                            {pkg.base_price > 0 && (
                              <span className="text-slate-500">/month</span>
                            )}
                            {pkg.is_trial && pkg.trial_days && (
                              <div className="mt-2 text-sm text-purple-600">
                                {pkg.trial_days}-day free trial
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <div className="space-y-3 mb-8">
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                          <span className="text-slate-700">
                            {pkg.executive_config?.limit_type === 'unlimited'
                              ? 'Unlimited Executives'
                              : `${pkg.executive_config?.included_count || 1} Executive${(pkg.executive_config?.included_count || 1) > 1 ? 's' : ''} included`
                            }
                          </span>
                        </div>
                        {pkg.ea_config && (
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-slate-700">
                              {pkg.ea_config.limit_type === 'unlimited'
                                ? 'Unlimited EAs per executive'
                                : pkg.ea_config.limit_type === 'included_plus_extra'
                                  ? `${pkg.ea_config.included_count || 1} EA${(pkg.ea_config.included_count || 1) > 1 ? 's' : ''} included${pkg.ea_config.cost_per_additional ? ` (+${formatPrice(pkg.ea_config.cost_per_additional, pkg.currency)}/additional)` : ''}`
                                  : pkg.ea_config.max_count > 1
                                    ? `Up to ${pkg.ea_config.max_count} EAs per executive`
                                    : '1 EA per executive'
                              }
                            </span>
                          </div>
                        )}
                        {(pkg.document_parsing_config?.period === 'unlimited' || pkg.document_parsing_config?.limit > 0) && (
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-slate-700">
                              {pkg.document_parsing_config.period === 'unlimited'
                                ? 'Unlimited document parsing'
                                : `${pkg.document_parsing_config.limit} document parses/${pkg.document_parsing_config.period}`
                              }
                            </span>
                          </div>
                        )}
                        {pkg.flight_verification_enabled && (
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-slate-700">Flight verification</span>
                          </div>
                        )}
                        {pkg.flight_alerts_config?.enabled && (
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-slate-700">
                              {pkg.flight_alerts_config.period === 'unlimited'
                                ? 'Unlimited flight alerts'
                                : `${pkg.flight_alerts_config.limit} flight alerts/${pkg.flight_alerts_config.period}`
                              }
                            </span>
                          </div>
                        )}
                        {pkg.teams_enabled && (
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-slate-700">Team collaboration</span>
                          </div>
                        )}
                        {pkg.rbac_enabled && (
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-slate-700">RBAC permissions</span>
                          </div>
                        )}
                        {pkg.sso_enabled && (
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-slate-700">SSO integration</span>
                          </div>
                        )}
                        {pkg.saml_sso_enabled && (
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-slate-700">SAML 2.0 SSO</span>
                          </div>
                        )}
                        {pkg.oidc_sso_enabled && (
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-slate-700">OIDC SSO</span>
                          </div>
                        )}
                        {pkg.audit_logs_enabled && (
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-slate-700">Audit logs</span>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => setShowEarlyAccessForm(true)}
                        className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                          index === 1 && packages.length >= 2
                            ? 'bg-purple-600 hover:bg-purple-700 text-white'
                            : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                        }`}
                      >
                        Get Started
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Feature Comparison Table */}
              {packages.length > 1 && (
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="p-6 border-b border-slate-200">
                    <h3 className="text-xl font-bold text-slate-900">Feature Comparison</h3>
                    <p className="text-slate-600 mt-1">See what's included in each plan</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left p-4 text-sm font-semibold text-slate-900 bg-slate-50 min-w-[200px]">
                            Feature
                          </th>
                          {packages.map((pkg) => (
                            <th key={pkg.id} className="text-center p-4 text-sm font-semibold text-slate-900 bg-slate-50 min-w-[140px]">
                              {pkg.name}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {COMPARISON_FEATURES
                          .map(category => ({
                            ...category,
                            features: category.features.filter(feature => anyPackageHasFeature(feature.key))
                          }))
                          .filter(category => category.features.length > 0)
                          .map((category) => (
                          <React.Fragment key={category.category}>
                            <tr className="bg-slate-50">
                              <td colSpan={packages.length + 1} className="p-3 text-sm font-semibold text-slate-700">
                                {category.category}
                              </td>
                            </tr>
                            {category.features.map((feature) => (
                              <tr key={feature.key} className="border-b border-slate-100">
                                <td className="p-4 text-sm text-slate-700">{feature.label}</td>
                                {packages.map((pkg) => {
                                  const display = getFeatureDisplay(pkg, feature.key);
                                  return (
                                    <td key={pkg.id} className="p-4 text-center">
                                      {display.type === 'check' ? (
                                        <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                                      ) : display.type === 'text' ? (
                                        <span className="text-sm font-medium text-green-600">{display.value}</span>
                                      ) : (
                                        <X className="w-5 h-5 text-slate-300 mx-auto" />
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Get started in minutes and transform how you manage executive travel.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-purple-600">1</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Create Your Account</h3>
              <p className="text-slate-600">
                Sign up and set up your organization. Add your executives and team members.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-purple-600">2</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Import Travel Details</h3>
              <p className="text-slate-600">
                Forward confirmation emails or manually add trips. Our AI handles the rest.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-purple-600">3</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Stay Updated</h3>
              <p className="text-slate-600">
                Get real-time updates and manage everything from your unified dashboard.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder's Note Section */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 sm:p-12">
            <p className="text-sm font-semibold uppercase tracking-wide text-purple-600 mb-3">
              From the Founder
            </p>
            <h2 className="text-3xl font-bold text-slate-900 mb-2">
              Why We Built Veyage
            </h2>
            <p className="text-slate-500 italic mb-8">By Jeremy Milikow</p>
            <div className="space-y-5 text-slate-700 leading-relaxed">
              <p>
                Executive assistants are responsible for keeping everything smooth and
                moving forward, even as their executives move constantly and schedules
                shift in real time. They are expected to keep plans on track across
                flights, hotels, ground transportation, and meetings, often while
                coordinating among multiple stakeholders and navigating last-minute
                changes.
              </p>
              <p>
                EAs use a variety of software tools to manage this reality, but most of
                their options were not built for this level of coordination.
              </p>
              <p>
                Many platforms offer some measure of visibility. They show bookings,
                itineraries, confirmations, and schedules. But seeing those elements is
                not the same as being able to manage the day as it evolves. The moment
                plans begin to shift—a meeting runs long, a flight changes, a pickup
                needs adjusting, a detail gets buried in an email thread—it becomes
                clear how much of executive travel still depends on fragmented
                coordination.
              </p>
              <p>
                That gap is what led us to build Veyage—a new command center for
                executive travel. Founded by operators with decades of experience
                supporting complex, high-stakes travel, Veyage gives executive
                assistants and EA teams greater visibility and control over the moving
                parts of their executives' itineraries.
              </p>
              <p>
                For some, that means more effective support around bookings. For
                others, it means more seamless coordination with team members or
                external partners. The details vary, but the underlying need is
                consistent: the ability to stay on top of executive travel as it
                evolves in real time. That is what Veyage is meant to provide—not just
                a record of what is supposed to happen, but a clearer way to manage
                what is happening now.
              </p>
              <p>
                We built Veyage to give EAs a stronger foundation for managing
                executive travel, and we look forward to shaping it further with the
                people who do this work every day.
              </p>
              <p>
                Veyage is now accepting qualified beta users. To see if you're a fit,{' '}
                <button
                  type="button"
                  onClick={() => setShowEarlyAccessForm(true)}
                  className="text-purple-600 font-semibold underline hover:text-purple-700"
                >
                  request access here
                </button>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="font-semibold text-slate-900">{item.question}</span>
                  {expandedFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-6 text-slate-600">
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-purple-600 to-indigo-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 text-sm font-medium mb-6">
            <Rocket className="w-4 h-4" />
            Early Access
          </div>
          <h2 className="text-3xl font-bold mb-6">
            Ready to Transform Your Executive Travel Management?
          </h2>
          <p className="text-lg text-purple-100 mb-8 max-w-2xl mx-auto">
            Be among the first to experience a smarter way to manage executive travel.
            Request early access and help shape the future of travel management.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowEarlyAccessForm(true)}
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-purple-600 hover:bg-purple-50 rounded-lg font-semibold text-lg transition-all"
            >
              Request Early Access
            </button>
            <button
              onClick={() => scrollToSection('pricing')}
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white/50 hover:bg-white/10 rounded-lg font-semibold text-lg transition-all"
            >
              Compare Plans
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm">
              Beta Preview - Pricing and features subject to change before general availability.
            </p>
          </div>
        </div>
      </footer>

      {/* Early Access Form Modal */}
      <EarlyAccessForm
        isOpen={showEarlyAccessForm}
        onClose={() => setShowEarlyAccessForm(false)}
      />
    </div>
  );
};

export default LandingPageV2;
